import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, cityLd, datasetLd } from '../seo';
import { api } from '../services/api';
import cities from '../data/cities.json';
import localitiesData from '../data/localities.json';
import type { City } from '../types';

interface CityData {
  slug: string;
  name: string;
  state: string;
  lat: number;
  lng: number;
  tier: number;
}

interface LocalityLite {
  slug: string;
  name: string;
  lat: number;
  lng: number;
}

interface CityStats {
  count: number;
  avg_hourly: number | null;
  avg_daily: number | null;
  avg_monthly: number | null;
}

export default function CityPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [city, setCity] = useState<CityData | null>(null);
  const [stats, setStats] = useState<CityStats | null>(null);
  const [metros, setMetros] = useState<City[]>([]);
  const [localities, setLocalities] = useState<LocalityLite[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    const c = (cities as CityData[]).find((x) => x.slug === slug);
    setCity(c ?? null);
    if (c) {
      fetch(`/api/stats/city/${slug}`)
        .then((r) => r.json())
        .then((j) => {
          if (j?.data?.stats) setStats(j.data.stats);
        })
        .catch(() => undefined);
      const locs = (localitiesData as { city: string; localities: LocalityLite[] }[]).find(
        (g) => g.city === slug
      )?.localities ?? [];
      setLocalities(locs);
    }
    api.getMetroCities().then((m) => setMetros(m)).catch(() => undefined);
    setUpdatedAt(new Date().toISOString());
  }, [slug]);

  if (!city) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900">City not found</h1>
        <p className="text-gray-600 mt-2">We don't have data for "{slug}" yet.</p>
        <Link to="/" className="text-parksy-600 hover:underline mt-4 inline-block">Open the map →</Link>
      </div>
    );
  }

  const pageTitle = `Parking in ${city.name} — Find & List Free | Parksy`;
  const description = `Find parking in ${city.name}, ${city.state}. Real prices, real spots, zero brokerage. List your driveway, garage, or apartment slot for free.`;
  const canonical = `https://parksy.rent/${city.slug}`;

  return (
    <>
      <Breadcrumbs
        items={[{ label: 'Home', to: '/' }, { label: `Parking in ${city.name}` }]}
        pageTitle={pageTitle}
        pageDescription={description}
        extraSchemas={[
          organizationLd(),
          webSiteLd(),
          cityLd({
            slug: city.slug,
            name: city.name,
            state: city.state,
            lat: city.lat,
            lng: city.lng,
            pinCount: stats?.count,
            avgMonthlyPrice: stats?.avg_monthly,
          }),
          datasetLd({
            name: `${city.name} parking stats`,
            description: `Live parking stats and average rates for ${city.name}, ${city.state}.`,
            url: canonical,
            citySlug: city.slug,
            updatedAt: updatedAt,
            rowCount: stats?.count,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Parking in {city.name}</h1>
          <p className="text-lg text-gray-600">
            Find parking in {city.name}, {city.state}. Real prices, real spots, zero brokerage.
          </p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-parksy-600">{stats?.count ?? '—'}</div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Spots listed</div>
          </div>
          <div className="bg-white border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {stats?.avg_monthly ? `₹${Math.round(stats.avg_monthly).toLocaleString('en-IN')}` : '—'}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Avg ₹/month</div>
          </div>
          <div className="bg-white border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {stats?.avg_daily ? `₹${Math.round(stats.avg_daily).toLocaleString('en-IN')}` : '—'}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Avg ₹/day</div>
          </div>
          <div className="bg-white border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">
              {stats?.avg_hourly ? `₹${Math.round(stats.avg_hourly).toLocaleString('en-IN')}` : '—'}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Avg ₹/hour</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          <Link
            to="/"
            className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-5 py-2.5 rounded-lg"
          >
            Open the map
          </Link>
          <Link
            to="/list-your-parking"
            className="inline-block bg-white border border-parksy-600 text-parksy-700 hover:bg-parksy-50 font-medium px-5 py-2.5 rounded-lg"
          >
            List your spot
          </Link>
          <Link
            to="/seekers"
            className="inline-block bg-white border border-blue-600 text-blue-700 hover:bg-blue-50 font-medium px-5 py-2.5 rounded-lg"
          >
            Find parking
          </Link>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How parking works in {city.name}</h2>
          <p className="text-gray-600 mb-3">
            {city.name} is a {city.tier === 1 ? 'Tier 1' : 'Tier 2'} metro with intense parking
            demand near offices, metro stations, and commercial hubs. Parksy helps you find
            residential driveways, apartment slots, and commercial lots that match your vehicle
            type, budget, and access needs.
          </p>
          <p className="text-gray-600">
            Use the map to drop a seeker pin (blue) or list your own spot (green). We match
            compatible pins daily and email both parties.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Common parking needs in {city.name}</h2>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm">
            {[
              'Monthly car parking near office',
              'Two-wheeler parking near metro station',
              'EV charging parking',
              '24/7 covered parking',
              'Apartment visitor parking',
              'Commercial operator listings',
            ].map((t) => (
              <li key={t} className="bg-white border rounded-lg px-3 py-2 text-gray-700">
                {t}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Parking in nearby cities</h2>
          <div className="flex flex-wrap gap-2 text-sm">
            {(metros.length > 0 ? metros : cities)
              .filter((m) => ('slug' in m ? m.slug !== city.slug : true))
              .slice(0, 12)
              .map((m) => {
                const target = 'slug' in m ? `/${(m as { slug: string }).slug}` : '#';
                return (
                  <Link
                    key={target + (m as { name?: string }).name}
                    to={target}
                    className="bg-white border rounded-lg px-3 py-2 text-gray-700 hover:border-parksy-500 hover:text-parksy-700"
                  >
                    Parking in {(m as { name: string }).name}
                  </Link>
                );
              })}
          </div>
        </section>

        {localities.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              Popular localities in {city.name}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
              {localities.map((l) => (
                <Link
                  key={l.slug}
                  to={`/${city.slug}/${l.slug}`}
                  className="bg-white border rounded-lg px-3 py-2 text-gray-700 hover:border-parksy-500 hover:text-parksy-700"
                >
                  Parking in {l.name}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
